import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useState,
  type ReactNode,
} from 'react';

import {
  withHistory,
  initHistory,
  canUndo as histCanUndo,
  canRedo as histCanRedo,
  isDirty,
} from './history';
import type { EditorModule } from './types';

// Estado do editor em TRÊS contextos separados — a razão é performance (§8.1):
//  - DocContext muda a cada edição; só o Canvas assina.
//  - DispatchContext é referência estável para sempre.
//  - MetaContext (ui + flags de undo/dirty) muda em seleção/edição; toolbar/Inspector.
// A `Cell` NÃO assina contexto nenhum: recebe props primitivas do Canvas. Assim
// digitar uma letra re-renderiza o Canvas (1 componente), não as 225 células.

export interface EditorDispatchApi {
  /** despacha uma ação do MÓDULO (ex.: TYPE_LETTER) pelo reducer com history */
  do(action: unknown): void;
  undo(): void;
  redo(): void;
  markSaved(): void;
  /** estado de UI local do módulo (ferramenta atual, foco, seleção) */
  setUi(updater: (prev: unknown) => unknown): void;
}

export interface EditorMeta {
  ui: unknown;
  canUndo: boolean;
  canRedo: boolean;
  dirty: boolean;
  /** false quando o passatempo já tem ranking — a grade é somente leitura */
  editavel: boolean;
}

const DocContext = createContext<unknown>(undefined);
const DispatchContext = createContext<EditorDispatchApi | null>(null);
const MetaContext = createContext<EditorMeta | null>(null);

interface Props<TDoc, TAction> {
  module: EditorModule<TDoc, TAction>;
  initialDoc: TDoc;
  editavel?: boolean;
  initialUi?: unknown;
  /** Doc importado nasce "não salvo". */
  initiallyDirty?: boolean;
  children: ReactNode;
}

export function EditorProvider<TDoc, TAction>({
  module,
  initialDoc,
  editavel = true,
  initialUi = {},
  initiallyDirty = false,
  children,
}: Props<TDoc, TAction>) {
  const reducer = useMemo(() => withHistory(module.reduce, module.coalesceKey), [module]);
  const [hist, dispatch] = useReducer(reducer, initialDoc, (doc: TDoc) => {
    const h = initHistory(doc);
    // Doc importado nasce "não salvo": nada dele existe no servidor ainda.
    return initiallyDirty ? { ...h, savedRevision: -1 } : h;
  });
  const [ui, setUiState] = useState<unknown>(initialUi);

  const setUi = useCallback((updater: (prev: unknown) => unknown) => setUiState(updater), []);

  const api = useMemo<EditorDispatchApi>(
    () => ({
      do: (action) => dispatch({ kind: 'do', action: action as TAction }),
      undo: () => dispatch({ kind: 'undo' }),
      redo: () => dispatch({ kind: 'redo' }),
      markSaved: () => dispatch({ kind: 'markSaved' }),
      setUi,
    }),
    [setUi],
  );

  const meta = useMemo<EditorMeta>(
    () => ({
      ui,
      canUndo: histCanUndo(hist),
      canRedo: histCanRedo(hist),
      dirty: isDirty(hist),
      editavel,
    }),
    [ui, hist, editavel],
  );

  return (
    <DocContext.Provider value={hist.present}>
      <DispatchContext.Provider value={api}>
        <MetaContext.Provider value={meta}>{children}</MetaContext.Provider>
      </DispatchContext.Provider>
    </DocContext.Provider>
  );
}

export function useEditorDoc<TDoc>(): TDoc {
  return useContext(DocContext) as TDoc;
}

export function useEditorDispatch(): EditorDispatchApi {
  const ctx = useContext(DispatchContext);
  if (!ctx) throw new Error('useEditorDispatch fora de um EditorProvider');
  return ctx;
}

export function useEditorMeta(): EditorMeta {
  const ctx = useContext(MetaContext);
  if (!ctx) throw new Error('useEditorMeta fora de um EditorProvider');
  return ctx;
}

/** Açúcar tipado para o estado de UI do módulo. */
export function useEditorUi<TUi>(): [TUi, (updater: (prev: TUi) => TUi) => void] {
  const { ui } = useEditorMeta();
  const { setUi } = useEditorDispatch();
  return [ui as TUi, setUi as (updater: (prev: TUi) => TUi) => void];
}
